export function needsShadowTriggerForOut ({stickyFor, onSticky} = {}) {
    return stickyFor && !['start', 'beforeStart'].includes(onSticky)
}

export function getScrubDuration({scrubDuration, durationUnit} = {}) {
    if (durationUnit !== 'pixel') {
        return 0
    }
    return scrubDuration
}

export function computeOutStartTrigger(trigger, sceneScale, containerOffset) {
    const scrubDuration = ['beforeStart', 'beforeEnd'].includes(trigger.onSticky) ? getScrubDuration(trigger) * sceneScale : 0
    const stickyMargin = (trigger.stickyMargin ?? 0) * sceneScale

    let modifier = ''
    let containerModifier = ''
    switch (trigger.stickyAnchor) {
        case 'top':
            modifier = `-=${stickyMargin + scrubDuration}`
            containerModifier = `-=${containerOffset.top}`
            break
        case 'center':
            modifier = `+=${-scrubDuration}`
            containerModifier = `+=${(containerOffset.top + -containerOffset.bottom) / 2}`
            break
        case 'bottom':
            modifier = `+=${stickyMargin - scrubDuration}`
            containerModifier = `-=${containerOffset.bottom}`
            break
    }

    return `${trigger.stickyAnchor}${modifier} ${trigger.stickyAnchor}${containerModifier}`
}

export function computeInStartTrigger (trigger, sceneScale, containerOffset) {
    const scrubDuration = ['beforeStart', 'beforeEnd'].includes(trigger.onSticky) ? getScrubDuration(trigger) * sceneScale : 0
    const stickyFor = (['end', 'beforeEnd'].includes(trigger.onSticky) && trigger.stickyForPx) ? trigger.stickyForPx * sceneScale : 0
    const stickyMargin = (trigger.stickyMargin ?? 0) * sceneScale

    let containerModifier = ''
    switch (trigger.stickyAnchor) {
        case 'top':
            containerModifier = `-=${containerOffset.top}`
            break
        case 'center':
            containerModifier = `+=${(containerOffset.top + -containerOffset.bottom) / 2}`
            break
        case 'bottom':
            containerModifier = `-=${containerOffset.bottom}`
            break
    }

    const operatorMap = {
        beforeStart: {
            top: `-=${scrubDuration + stickyMargin}`,
            center: `-=${scrubDuration}`,
            bottom: `+=${stickyMargin - scrubDuration}`
        },
        start: {
            top: `-=${stickyMargin}`,
            center: '+=0',
            bottom: `+=${stickyMargin}`
        },
        beforeEnd: {
            top: `+=${stickyFor - stickyMargin - scrubDuration}`,
            center: `+=${stickyFor - scrubDuration}`,
            bottom: `+=${stickyFor + stickyMargin - scrubDuration}`
        },
        end: {
            top: `+=${stickyFor - stickyMargin}`,
            center: `+=${stickyFor}`,
            bottom: `+=${stickyFor + stickyMargin}`
        },
    }

    const modifier = operatorMap[trigger.onSticky]?.[trigger.stickyAnchor]
    return `${trigger.stickyAnchor}${modifier} ${trigger.stickyAnchor}${containerModifier}`
}

const CANVAS_STICKY_ANCHORS = ['top', 'center', 'bottom']

/**
 * Reads the sticky settings of a canvas-sticky element from what the publish already renders on it
 * (applyStickyOnGrid): the anchor as an `anchored-*` class, margin and sticky duration in design px
 * as CSS vars. Returns the same fields addStickyTrigger publishes on a sticky scroll animation, so
 * the result can go straight into computeInStartTrigger.
 *
 * `--sticky-for` is only trusted together with the `sticky-for` class: custom properties inherit,
 * so a sticky group without a duration would otherwise pick up the duration of a sticky ancestor.
 */
export function readCanvasSticky (classList, style) {
    return {
        stickyAnchor: CANVAS_STICKY_ANCHORS.find(anchor => classList.contains(`anchored-${anchor}`)) ?? 'top',
        stickyMargin: parseFloat(style.getPropertyValue('--sticky-margin-px')) || 0,
        stickyForPx: classList.contains('sticky-for') ? (parseFloat(style.getPropertyValue('--sticky-for')) || '') : '',
    }
}

/**
 * End of a scrub that lasts as long as the element is sticky, for an element without a sticky
 * duration: it then sticks until the scene ends, so the end trigger is the scene and the end is the
 * moment the scene's bottom passes the pinned element. Use with `endTrigger: <scene element>`.
 */
export function computeStickyUntilSceneEnd (trigger, elementHeight, sceneScale, containerOffset) {
    let modifier = ''
    switch (trigger.stickyAnchor) {
        case 'top':
            modifier = `+=${(trigger.stickyMargin ?? 0) * sceneScale + elementHeight + containerOffset.top}`
            break
        case 'center':
            modifier = `+=${(elementHeight / 2) - containerOffset.bottom / 2}`
            break
        case 'bottom':
            modifier = `-=${(trigger.stickyMargin ?? 0) * sceneScale + containerOffset.bottom}`
            break
    }
    return `bottom ${trigger.stickyAnchor}${modifier}`
}
